import React from "react";
import { Helmet } from "react-helmet";
import "./Legal.css";

export const PrivacyPolicy = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/privacy`;
  const description = "FlintHost Privacy Policy and GDPR disclosures.";
  return (
    <div className="legal-page">
      <Helmet>
        <title>FlintHost | Privacy Policy</title>
        <meta name="description" content={description} />
        <meta property="og:title" content="FlintHost | Privacy Policy" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/img/Logo2.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FlintHost | Privacy Policy" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${origin}/img/Logo2.png`} />
      </Helmet>
      <div className="legal-hero">
        <p className="legal-eyebrow">Legal</p>
        <h1>Privacy Policy</h1>
        <p>Effective date: 1 January 2026</p>
      </div>
      <div className="legal-nav">
        <a href="/tos">Terms of Service</a>
        <a href="/privacy" aria-current="page">Privacy Policy</a>
        <a href="/acceptable-use">Acceptable Use</a>
        <a href="/refunds">Refund Policy</a>
      </div>
      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Who we are</h2>
          <p>
            FlintHost (“we”, “us”) provides game server hosting services for Minecraft, Hytale, and other supported games.
            For GDPR purposes we are the data controller for account and billing information. Contact us at
            <a href="mailto:contact@flinthost.com"> contact@flinthost.com</a>.
          </p>
        </section>
        <section className="legal-section">
          <h2>2. Data we collect</h2>
          <ul>
            <li>Account details (name, email, username, authentication data).</li>
            <li>Billing details (address, VAT ID, transaction metadata).</li>
            <li>Service data (server configuration, logs, backups you create).</li>
            <li>Support communications (tickets, chat, email).</li>
            <li>Usage data (device, browser, IP address, timestamps, diagnostics).</li>
          </ul>
        </section>
        <section className="legal-section">
          <h2>3. How we use data</h2>
          <ul>
            <li>Provide and maintain hosting services and panel access.</li>
            <li>Process payments, prevent fraud, and meet legal obligations.</li>
            <li>Respond to support requests and service communications.</li>
            <li>Improve reliability, security, and performance of the platform.</li>
          </ul>
        </section>
        <section className="legal-section">
          <h2>4. Lawful bases (GDPR)</h2>
          <ul>
            <li>Contract: providing the services you request.</li>
            <li>Legal obligation: tax and accounting requirements.</li>
            <li>Legitimate interests: service security, abuse prevention, and analytics.</li>
            <li>Consent: marketing emails or optional cookies (where applicable).</li>
          </ul>
        </section>
        <section className="legal-section">
          <h2>5. Sharing & subprocessors</h2>
          <p>
            We share data with trusted vendors for hosting infrastructure, payment processing, and support tooling.
            These vendors act as processors under contract and only process data as instructed.
          </p>
        </section>
        <section className="legal-section">
          <h2>6. International transfers</h2>
          <p>
            If data is transferred outside the EEA, we use safeguards such as Standard Contractual Clauses and
            vendor security reviews.
          </p>
        </section>
        <section className="legal-section">
          <h2>7. Retention</h2>
          <p>
            We keep data only as long as needed for service delivery, legal obligations, and legitimate interests.
            You may request deletion after account closure, subject to legal retention requirements.
          </p>
        </section>
        <section className="legal-section">
          <h2>8. Your rights</h2>
          <ul>
            <li>Access, correction, deletion, and portability of your data.</li>
            <li>Restriction or objection to processing.</li>
            <li>Withdraw consent at any time where processing relies on consent.</li>
            <li>File a complaint with your local data protection authority.</li>
          </ul>
        </section>
        <section className="legal-section">
          <h2>9. Cookies</h2>
          <p>
            We use essential cookies for authentication and service reliability. Optional analytics cookies are used
            only where permitted by law and may be disabled through browser controls.
          </p>
        </section>
        <section className="legal-section">
          <h2>10. Security</h2>
          <p>
            We implement technical and organizational measures to protect data, including access controls,
            encryption in transit, and monitoring.
          </p>
        </section>
        <section className="legal-section">
          <h2>11. Contact</h2>
          <p>
            For privacy requests, contact us at <a href="mailto:contact@flinthost.com">contact@flinthost.com</a>.
          </p>
        </section>
      </div>
    </div>
  );
};
